import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authAPI, meetingAPI } from '../api/client';
import { alertDialog, confirmDialog } from '../components/AppDialog';
import RecordingAliveCheck from '../components/RecordingAliveCheck';
import {
  acquireRecLock,
  countChunks,
  deleteRecording,
  discardTailSegment,
  estimateDurationSec,
  extForMime,
  isForeignExpired,
  isRecordingPersistSupported,
  lastSeq,
  listSessions,
  newPersistId,
  patchSession,
  putChunk,
  putSession,
  readChunkEntries,
  splitSegments,
} from '../lib/recordingStore';
import { readRawAudioPref, recordingAudioConstraints } from '../lib/recordingPrefs';
import { readPcAudioPref, acquirePcAudio, isSameInputDevice, mixStreams, registerStreamCleanup, stopRecordingStream, onTrackEnded, hasLiveAudio } from '../lib/recordingPcAudio';
import { MEETING_LANGUAGE_OPTIONS } from '../utils/meetingUtil';

const RecordingContext = createContext(null);
const MAX_PENDING_UPLOADS = 5;
const UPLOAD_AUTO_RETRY_DELAY_MS = 3500;
const UPLOAD_ERROR_DETAIL_MAX = 80;
// 새로고침·401·탭 닫기로 페이지가 죽어도 조각을 잃지 않게 IndexedDB 에 한 벌 더 쌓고,
// 주인 없는(= Web Lock 이 풀린) 녹음만 로그인 상태에서 한 번 묻는다.
const RECOVERY_SCAN_INTERVAL_MS = 1500;
const RECOVERY_LOCK_RETRY_MS = 2000;

// ── 무음 자동 중지 ─────────────────────────────────────────────────────────
// 회의가 끝났는데 중지를 깜빡하면 빈 녹음이 그대로 STT 로 넘어간다(GPU 낭비 + '완료인데 내용 0').
// 볼륨 미터(RMS)를 그대로 재사용한다. 미터는 0.015 미만을 0으로 눌러 내보내므로 그보다 살짝 위를 쓴다.
// 듣기만 하는 조용한 구간에서 오작동하지 않도록 7분을 기다리고, 자동 중지 전 1분을 더 준다.
const SILENCE_LEVEL = 0.02;
const SILENCE_PROMPT_MS = 7 * 60 * 1000;
const SILENCE_AUTOSTOP_MS = 60 * 1000;
const SILENCE_CHECK_MS = 1000;

// 🔴 2026-08-18 사고: 52분짜리 무음 녹음에서 위 자동중지가 **한 번도 안 걸렸다**.
// 그 오디오로 판정식을 그대로 재현하니 3,115회 측정이 전부 level=0 이었다 — 즉 판정이 틀린 게
// 아니라 **판정 자체가 안 돌았다**. 미터(analyser) 초기화가 실패하면 startRecMeter 가 자체
// try/catch 로 삼키고, checkSilence 는 level==null 이라 매 틱 그냥 return 한다 →
// 녹음은 정상 진행되고 무음 가드만 **조용히 사라진다**(사용자에게도 서버에도 신호가 없다).
// 그래서 ① 미터가 죽으면 되살리고 안 되면 알린다 ② 시작부터 소리가 없으면 일찍 알린다.
const METER_DEAD_MS = 10 * 1000;       // 레벨을 이만큼 못 읽으면 미터가 죽은 것으로 본다
const METER_RETRY_MAX = 2;             // 되살리기 시도 횟수(그 뒤엔 사용자에게 알린다)
const NO_SOUND_EARLY_MS = 20 * 1000;   // 시작 후 이만큼 소리가 없으면 바로 알린다(52분을 날리지 않게)

// ── 30분마다 «아직 녹음 중인가요?» 확인 ─────────────────────────────────────
// 마스터 지시(2026-10-02): 30분마다 한 번 묻는다. 🔴 묻는 동안에도 녹음은 계속된다 —
// 이 경로는 녹음을 멈추거나 일시정지하지 않고, 답이 없어도 자동 종료하지 않는다
// (무음 자동중지는 위 가드가 종전대로 따로 돈다).
// 일시정지 시간은 세지 않는다(getRecordingAudioMs 가 일시정지를 뺀 실제 녹음 시간이다).
const ALIVE_CHECK_MS = 30 * 60 * 1000;
const ALIVE_NOTIF_TAG = 'aigw-rec-alive';
// 서비스워커 핑 응답 대기. 답이 없으면 **옛 SW** 로 보고 SW 알림을 쓰지 않는다(아래 notifyAliveCheck).
const ALIVE_SW_PING_TIMEOUT_MS = 1500;

function aliveNotifyBody(minutes) {
  return `녹음 ${minutes}분째입니다. 아직 녹음 중인가요? (녹음은 계속되고 있습니다)`;
}

function stringifyUploadDetail(detail) {
  if (detail == null) return '오류';
  if (typeof detail === 'string') return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

// 이어 녹음(구간 2개 이상) 업로드에서 합치기가 실패했다(422 — 다시 해도 실패한다).
const MERGE_FAILED_MESSAGE = '오디오 이어붙이기에 실패했습니다 — 녹음은 이 브라우저에 남아 있습니다. 관리자에게 알려 주세요';
const MERGE_FAILED_DISCARD_CONFIRM = '이 녹음의 모든 구간(앞서 복구한 녹음 포함)이 지워집니다';
const SEGMENTS_NOT_RECEIVED_MESSAGE = '서버가 이어 녹음 구간을 받지 못했습니다(첫 구간만 저장된 노트가 있습니다) — 관리자에게 알려 주세요';
const RESUME_GAP_MESSAGE = '새로고침부터 지금까지는 녹음되지 않았습니다. 여기서부터 이어서 녹음합니다.';
const RESUME_NO_PERSIST_MESSAGE = '새로고침 대비 보관을 하지 못합니다 — 마치면 바로 업로드해 주세요';
// 이어 녹음 구간 상한(서버 합치기 상한 20개와 같다).
const REC_SEGMENTS_MAX = 20;

// 서버가 이어붙일 수 있는 형식만 이어 녹음을 허용한다(audio/aac 는 서버가 400 으로 거부한다).
function isResumableMime(mime) {
  const t = String(mime || '').toLowerCase();
  return t.includes('webm') || t.includes('mp4') || t.includes('ogg');
}

function uploadFailureMessage(err, multiSegment = false) {
  const status = err?.response?.status;
  if (status === 422 && multiSegment) return MERGE_FAILED_MESSAGE;
  if (status) {
    const detail = stringifyUploadDetail(err.response?.data?.detail || err.message).slice(0, UPLOAD_ERROR_DETAIL_MAX);
    return `업로드 실패(${status}: ${detail}) — 재시도하세요`;
  }
  return '업로드 실패(네트워크 연결 끊김) — 재시도하세요';
}

function shouldAutoRetryUpload(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

function sleep(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

// «PC 소리도 함께 녹음»을 켰는데 PC 소리를 못 받아 마이크만으로 시작했을 때의 안내.
function pcAudioFallbackMessage(pc) {
  switch (pc?.reason) {
    case 'no-audio':
      return 'PC 소리가 공유되지 않아 마이크만 녹음합니다. 공유 창에서 "전체 화면" 또는 "탭"을 고르고 "오디오 공유"를 켜야 합니다. (창 공유에는 소리가 없습니다)';
    case 'cancelled':
      return 'PC 소리 공유를 취소해 마이크만 녹음합니다. 이 옵션은 넓은 화면의 새 노트에서 끌 수 있습니다.';
    case 'no-device':
      return '고른 PC 소리 장치를 찾지 못해 마이크만 녹음합니다. 새 노트 화면에서 장치를 다시 골라 주세요.';
    case 'same-device':
      return '고른 PC 소리 장치가 지금 마이크와 같은 장치라 마이크만 녹음합니다(같은 소리가 두 번 섞이지 않게). PC 소리를 넣으려면 새 노트 화면에서 "스테레오 믹스" 같은 다른 입력 장치를 골라 주세요.';
    default:
      return 'PC 소리를 가져오지 못해 마이크만 녹음합니다' + (pc?.message ? `: ${pc.message}` : '.');
  }
}

// 복구 미리보기(복구 스캔 · 이어 녹음 취소 뒤 되살리기 공용). split = splitSegments 결과(조각 0개 구간 없음).
// 재생은 첫 구간 하나만 한다(구간별 플레이어는 두지 않는다) — object URL 도 첫 구간 하나뿐이다.
function buildRecoveredPreview(row, split, persistId, overrides = {}) {
  const startedAt = Number(row?.startedAt) || Date.now();
  const segments = split.map((seg) => ({
    blob: new Blob(seg.blobs, { type: seg.mime }),
    mime: seg.mime,
    chunkCount: seg.blobs.length,
  }));
  const chunkTotal = segments.reduce((n, seg) => n + seg.chunkCount, 0);
  const blob = segments[0].blob;
  return {
    blob,
    url: URL.createObjectURL(blob),
    filename: `recording_${startedAt}.${extForMime(segments[0].mime)}`,
    title: overrides.title ?? (row?.title || ('녹음 ' + new Date(startedAt).toLocaleString('ko-KR'))),
    notes: overrides.notes ?? (Array.isArray(row?.notes) ? row.notes : []),
    durationSec: estimateDurationSec(chunkTotal),
    persistId,
    recovered: true,
    resumable: true,
    segments,
    // 이어 녹음 경계 기록의 «이전» 목록(정규화 — 옛 행은 [{startSeq:0, mime:row.mime}]).
    persistSegments: split.map((seg) => ({ startSeq: seg.startSeq, mime: seg.mime })),
  };
}

// [이어 녹음]을 보일 미리보기인가 — 복구물(resumable)·보관 가능·구간 20개 미만·이어붙일 수 있는 형식.
function canResumePreview(pv) {
  if (!pv || !pv.resumable || !pv.persistId) return false;
  if (!isRecordingPersistSupported()) return false;
  const segments = Array.isArray(pv.segments) ? pv.segments : [];
  return segments.length > 0
    && segments.length < REC_SEGMENTS_MAX
    && segments.every((seg) => isResumableMime(seg?.mime));
}

function formatRecoveryLength(sec) {
  const total = Math.max(0, Math.round(Number(sec) || 0));
  const min = Math.floor(total / 60);
  const rest = total % 60;
  return min > 0 ? `${min}분 ${rest}초` : `${rest}초`;
}

// 복구 제안 문구. 🔴 녹음 제목은 넣지 않는다 — 대화상자가 뜬 채 다른 탭에서 로그아웃하면
// 그 문구가 다음 사람에게 그대로 보일 수 있다(AppDialog 에는 닫는 API 가 없다).
function recoveryPromptMessage(row, chunkCount) {
  const startedAt = Number(row?.startedAt) || Date.now();
  const when = new Date(startedAt).toLocaleString('ko-KR');
  const length = formatRecoveryLength(estimateDurationSec(chunkCount));
  const state = row?.state;
  const reason = state === 'uploading'
    ? '업로드 도중 중단됐습니다. 이미 저장됐을 수 있으니 회의록 목록에서 먼저 확인해 주세요.'
    : state === 'stopped'
      ? '업로드되지 않았습니다.'
      : '녹음 도중 페이지가 닫혀 저장되지 못했습니다.';
  const partial = row?.partial ? ' (저장 공간 부족으로 일부만 보관됨)' : '';
  return `${when}에 시작한 녹음(약 ${length})이 ${reason}${partial}\n\n`
    + '[복구]하면 녹음 미리보기로 열립니다 — AI 노트 화면으로 바로 이동하니 들어 보고 [업로드]로 저장하세요.'
    + ' 필요 없으면 미리보기의 [취소]로 지울 수 있습니다.'
    + ' [나중에]를 누르면 보관했다가 다음에 다시 묻습니다.';
}

// ── 녹음 메타(폴더·화자분리·참석자·인식 언어) ──────────────────────────────
// 🔴 참석자·인식 언어의 값 규칙은 **B3 서버와 짝이다** — `backend/routers/meetings.py` 의
//    `_participants_clean`·`_language_clean`(설계 patch_meeting_note_meta_b3_backend.md §2)과
//    같은 규칙을 여기서 미리 적용한다. **한쪽을 바꾸면 둘 다 바꾼다.**
//    왜 서버만 믿으면 안 되나: 서버는 규칙 밖 값(이름 40자 초과·31명·문자열 아닌 항목·
//    목록에 없는 언어)을 조용히 자르지 않고 **400** 으로 막는다. 400 은 자동 재시도 대상이
//    아니고(shouldAutoRetryUpload 는 status >= 500 만 재시도), 수동 재시도·복구는 **같은 meta 를
//    그대로 다시 보낸다** → 여기서 정리하지 않으면 그 녹음은 버리는 것 말고는 올릴 길이 없다.
// 목록 정본은 utils/meetingUtil `MEETING_LANGUAGE_OPTIONS`(새 노트 화면과 같은 목록).
export const REC_LANGUAGE_OPTIONS = MEETING_LANGUAGE_OPTIONS;
const REC_LANGUAGES = REC_LANGUAGE_OPTIONS.map((l) => l.code);   // 서버 MEETING_LANGUAGES
const REC_PARTICIPANT_NAME_MAX = 40;                      // 서버 PARTICIPANT_NAME_MAX
const REC_PARTICIPANTS_MAX = 30;                          // 서버 PARTICIPANTS_MAX
const REC_TITLE_MAX = 120;                                // RecordingView 제목 input maxLength

// 🔴 매번 **새 객체·새 배열**을 돌려준다. 모듈 상수를 펼쳐 쓰면 enqueueUpload 의 얕은 복사
//    (`{ ...payload.meta }`)로도 participants 배열이 큐 항목들 사이에 공유된다.
function emptyRecMeta() {
  return { folderId: null, folderName: '', diarize: false, participants: [], language: null };
}

function cleanRecLanguage(value) {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase();
  return REC_LANGUAGES.includes(code) ? code : null;
}

function cleanRecParticipants(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const clean = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;   // 서버는 400 — 여기서는 버린다
    // 🔴 순서를 고정한다: trim → 40자 자르기(`Array.from` = 코드포인트 기준이라 서버 `len` 과 같고
    //    이모지를 반으로 쪼개지 않는다) → 다시 trim(자른 끝이 공백일 수 있다) → 빈 값 버림 →
    //    중복 제거(자른 **뒤**라 앞 40자가 같은 두 이름은 하나가 된다) → 30명까지.
    const name = Array.from(item.trim()).slice(0, REC_PARTICIPANT_NAME_MAX).join('').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    clean.push(name);
    if (clean.length >= REC_PARTICIPANTS_MAX) break;
  }
  return clean;
}

// 🔴 제목은 여기 넣지 않는다 — 제목의 정본은 recTitleRef 이고 보관은 flushPersist 가 한다.
function cleanRecMeta(meta) {
  return {
    ...emptyRecMeta(),
    folderId: meta?.folderId ?? null,
    folderName: meta?.folderName || '',
    diarize: !!meta?.diarize,
    participants: cleanRecParticipants(meta?.participants),
    language: cleanRecLanguage(meta?.language),
  };
}

export function RecordingProvider({ children }) {
  const [recording, setRecording] = useState(false);
  const [recStarting, setRecStarting] = useState(false);
  const [recFinalizing, setRecFinalizing] = useState(false);
  const [recPreview, setRecPreview] = useState(null);
  const [recTitle, setRecTitle] = useState('');
  const [recLevel, setRecLevel] = useState(0);
  const [recPaused, setRecPaused] = useState(false);
  const [recPauseSupported, setRecPauseSupported] = useState(false);
  const [recordingNotes, setRecordingNotes] = useState([]);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploadVersion, setUploadVersion] = useState(0);
  const [floatingError, setFloatingError] = useState('');
  const [recordingScreenMinimized, setRecordingScreenMinimized] = useState(false);
  // 복구 대화상자에서 [복구]를 고를 때만 1 씩 오른다 — FloatingRecorder 가 보고 AI 노트(/meetings)로 이동한다(#1869).
  // 녹음을 멈춰 만든 미리보기·이어 녹음 취소 뒤 되살린 미리보기에서는 오르지 않는다.
  const [recoverNavSeq, setRecoverNavSeq] = useState(0);
  // 무음 자동 중지: 회의가 끝났는데 중지를 깜빡하는 경우를 잡는다.
  // 실측 근거 — prod 에 68분짜리 전사 0자(-91dB) 녹음이 남아 있었다(회의 후 계속 돈 것).
  // null = 정상, 숫자 = 자동 중지까지 남은 초.
  const [silenceWarnSec, setSilenceWarnSec] = useState(null);
  // 무음 가드가 **꺼진 채로 녹음이 계속되는 것**을 막기 위한 표시(2026-08-18 사고).
  // meterFailed = 레벨을 못 읽는다(= 자동중지가 동작하지 않는다) / noSoundWarn = 시작부터 소리가 없다.
  const [meterFailed, setMeterFailed] = useState(false);
  const [noSoundWarn, setNoSoundWarn] = useState(false);
  // 30분 확인: null = 물을 것 없음, 숫자 = 그 분(分)에 물었고 아직 답하지 않았다.
  const [aliveCheckMin, setAliveCheckMin] = useState(null);
  // 녹음 바 «언어» 표시용. 정본은 recMetaRef.current.language / session.meta.language 다(setRecLanguage).
  const [recLanguage, setRecLanguageState] = useState(null);

  const recStartRef = useRef(0);
  const mediaRef = useRef(null);
  const recPreviewRef = useRef(null);
  const recSessionSeqRef = useRef(0);
  const recSessionRef = useRef(null);
  const recStartingRef = useRef(false);
  const recFinalizingRef = useRef(false);
  const recTitleRef = useRef('');
  const recPausedMsRef = useRef(0);
  const recPauseStartedRef = useRef(0);
  const recPausedRef = useRef(false);
  const recLevelRafRef = useRef(null);
  const recAudioCtxRef = useRef(null);
  const recAnalyserRef = useRef(null);
  const recSourceRef = useRef(null);
  const recLevelDataRef = useRef(null);
  const recMetaRef = useRef(emptyRecMeta());
  const pendingUploadsRef = useRef([]);
  const pendingUploadSeqRef = useRef(0);
  const pendingUploadRunsRef = useRef({});
  const pendingUploadRetryTimersRef = useRef({});
  const uploadPendingItemRef = useRef(null);
  // 무음 판정용. 소리가 들리면 lastSound 를 갱신하고, 조용한 시간이 임계치를 넘으면 경고→자동 중지.
  const lastSoundAtRef = useRef(0);
  const silenceWarnStartedRef = useRef(0);
  const silenceWarnRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const stopRecRef = useRef(null);        // startRecMeter 가 stopRec 보다 먼저 정의돼 ref 로 우회
  const startRecMeterRef = useRef(null);  // checkSilence 안에서 미터를 되살리려면 ref 가 필요(같은 이유)
  const recMeterStreamRef = useRef(null); // 되살릴 때 쓸 원본 스트림
  const meterDeadSinceRef = useRef(0);    // 레벨을 처음 못 읽은 시각(0=정상)
  const meterRetryRef = useRef(0);        // 되살리기 시도 횟수
  const heardSoundRef = useRef(false);    // 이 녹음에서 한 번이라도 소리가 있었나
  const meterFailedRef = useRef(false);
  const noSoundWarnRef = useRef(false);
  const recBaseTitleRef = useRef('');     // 녹음 중 탭 제목을 바꾸기 전 원래 제목
  // 30분 확인용. aliveNextMs = 다음에 물을 녹음 시간(ms) · aliveCheckMinRef = 화면 상태의 미러
  // (getMe 왕복 뒤 '아직 답하지 않았나'를 렌더와 무관하게 판정해야 한다) · aliveNotif = 페이지 알림.
  const aliveNextMsRef = useRef(ALIVE_CHECK_MS);
  const aliveCheckMinRef = useRef(null);
  const aliveNotifRef = useRef(null);
  // 새로고침 대비 임시 보관(IndexedDB) — persistId → Web Lock 해제 함수.
  const persistLocksRef = useRef(new Map());
  const recoveryRef = useRef({ done: false, running: false, epoch: 0 });
  const persistWarnedRef = useRef(false);   // 저장 공간 경고는 1회만

  const setRecStartingLocked = useCallback((next) => {
    recStartingRef.current = next;
    setRecStarting(next);
  }, []);

  const setRecFinalizingLocked = useCallback((next) => {
    recFinalizingRef.current = next;
    setRecFinalizing(next);
  }, []);

  const updateRecTitle = useCallback((value) => {
    recTitleRef.current = value;
    setRecTitle(value);
  }, []);

  // ── 30분 확인 ──────────────────────────────────────────────────────────────
  // 🔴 아래 헬퍼들도 ref·모듈 함수만 쓴다(위 184줄 규칙) — identity 가 렌더마다 바뀌면
  //    resetCurrentRecSession → forceClearRecording 의 cleanup 이 돌아 녹음이 끊긴다.
  const setAliveCheckMinLocked = useCallback((next) => {
    aliveCheckMinRef.current = next;
    setAliveCheckMin(next);
  }, []);

  // 확인·세션 종료 뒤 알림 센터에 «아직 녹음 중인가요?» 가 남지 않게 닫는다(전부 베스트에포트).
  const closeAliveNotifications = useCallback(() => {
    const notif = aliveNotifRef.current;
    aliveNotifRef.current = null;
    try { notif?.close?.(); } catch {}
    try {
      const container = typeof navigator === 'undefined' ? null : navigator.serviceWorker;
      const pending = container?.getRegistration?.();
      pending?.then?.((reg) => reg?.getNotifications?.({ tag: ALIVE_NOTIF_TAG }))
        ?.then?.((list) => {
          (list || []).forEach((item) => { try { item.close(); } catch {} });
        })
        ?.catch?.(() => {});
    } catch {}
  }, []);

  // 이 탭에 붙은 서비스워커가 **새 SW**(= focusOnly 분기를 가진 것)인지 핑으로 확인한다.
  // 🔴 옛 SW 는 알림 클릭에서 창을 navigate 해 녹음 중인 탭을 다시 띄운다(= 새로고침, 녹음 소실).
  //    답이 없으면 null 을 돌려 SW 알림을 쓰지 않는다. 핑 상대는 controller 가 아니라 reg.active 다
  //    (클릭을 처리하는 쪽이 reg.active 이고, 강력 새로고침한 탭은 controller 가 null 이다).
  const pingAliveServiceWorker = useCallback((worker) => new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        const data = event?.data;
        if (data && data.type === 'aigw-sw-pong' && data.focusOnly) finish({ clientId: data.clientId ?? null });
        else finish(null);
      };
      worker.postMessage({ type: 'aigw-sw-ping' }, [channel.port2]);
      setTimeout(() => finish(null), ALIVE_SW_PING_TIMEOUT_MS);
    } catch {
      finish(null);
    }
  }), []);

  // «내정보 → 알림 켜기»가 켜져 있을 때만 OS 알림을 한 번 보낸다(권한은 **묻지 않는다**).
  // 🔴 모바일(Android Chrome·iOS 홈화면 앱)에서는 페이지 `new Notification` 이 아예 통하지 않으므로
  //    서비스워커 알림을 먼저 쓰고, 핑에 답하지 않는 옛 SW 라면 페이지 알림으로 내려온다.
  const notifyAliveCheck = useCallback(async (minutes, session) => {
    try {
      if (typeof window === 'undefined' || typeof window.Notification !== 'function') return;
      if (window.Notification.permission !== 'granted') return;
      const nextMs = aliveNextMsRef.current;
      // getMe 왕복 중 [계속 녹음]·[종료 후 업로드]를 누르면 늦은 알림을 보내지 않는다.
      const stillPending = () => (
        recSessionRef.current === session
        && !recFinalizingRef.current
        && aliveNextMsRef.current === nextMs
        && aliveCheckMinRef.current === minutes
      );
      let enabled = false;
      try {
        const res = await authAPI.getMe();
        enabled = !!res?.data?.notifications_enabled;
      } catch {
        return;
      }
      if (!enabled || !stillPending()) return;
      const options = {
        body: aliveNotifyBody(minutes),
        icon: '/icon-192.png',
        tag: ALIVE_NOTIF_TAG,
        renotify: true,
      };
      // ① 서비스워커 알림 — 새 SW 가 확인됐을 때만.
      if (typeof navigator !== 'undefined' && navigator.serviceWorker && typeof MessageChannel === 'function') {
        let reg = null;
        try {
          reg = await navigator.serviceWorker.getRegistration();
        } catch {
          return;   // 등록을 못 읽었다 — 페이지 알림으로 넘어가지 않는다
        }
        if (reg && reg.active) {
          const pong = await pingAliveServiceWorker(reg.active);
          if (pong) {
            if (!stillPending()) return;
            try {
              // clientId = 핑에 답한 SW 가 알려준 이 탭의 id. 클릭 때 **녹음 탭**을 먼저 찾는 데 쓴다.
              await reg.showNotification('녹음 확인', {
                ...options,
                data: { focusOnly: true, clientId: pong.clientId ?? null },
              });
            } catch {}
            return;
          }
        }
      }
      // ② 페이지 알림 — SW 가 없거나 reg.active 가 없거나 옛 SW(핑 무응답)일 때.
      if (!stillPending()) return;
      try {
        const notif = new window.Notification('녹음 확인', options);
        notif.onclick = () => {
          // 🔴 navigate 하지 않는다 — 이 탭이 다시 뜨면 메모리의 녹음이 끊긴다.
          try { window.focus(); } catch {}
          try { notif.close(); } catch {}
        };
        aliveNotifRef.current = notif;
      } catch {}
    } catch {}
  }, [pingAliveServiceWorker]);

  // «계속 녹음» — 🔴 미확인 표시만 지운다. 다음 확인 시점(aliveNextMsRef)은 tick 이 이미 정해 뒀으므로
  //               건드리지 않는다(건드리면 확인한 지 몇 분 만에 다시 묻는다). 녹음에는 손대지 않는다.
  const confirmAlive = useCallback(() => {
    setAliveCheckMinLocked(null);
    closeAliveNotifications();
  }, [closeAliveNotifications, setAliveCheckMinLocked]);

  // ── 새로고침·401·탭 닫기 대비 임시 보관(IndexedDB + Web Locks) ─────────────
  // 🔴 여기 헬퍼들은 ref·모듈 함수·같은 규칙의 다른 헬퍼만 쓴다. 의존성이 렌더마다 바뀌면
  //    forceClearRecording 의 identity 가 바뀌고 그 cleanup 이 돌아 녹음이 끊긴다.
  const currentUserId = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const id = user?.id;
      return id === undefined || id === null || id === '' ? null : String(id);
    } catch {
      return null;
    }
  }, []);

  const isRecordingIdle = useCallback(() => (
    !recSessionRef.current && !recStartingRef.current && !recFinalizingRef.current && !recPreviewRef.current
  ), []);

  const releasePersistLock = useCallback((persistId) => {
    if (!persistId) return;
    const release = persistLocksRef.current.get(persistId);
    persistLocksRef.current.delete(persistId);
    try { release?.(); } catch {}
  }, []);

  const releaseAllPersistLocks = useCallback((keepIds) => {
    const keep = keepIds instanceof Set ? keepIds : new Set(keepIds || []);
    [...persistLocksRef.current.keys()].forEach((id) => {
      if (!keep.has(id)) releasePersistLock(id);
    });
  }, [releasePersistLock]);

  // 🔴 보관 레코드 삭제는 여기서만 한다 — 업로드 서버 성공 · 사용자 삭제/취소 ·
  //    스캔의 빈 레코드/30일 넘은 남의 레코드. forceClear 류는 절대 지우지 않는다.
  const dropPersisted = useCallback(async (persistId) => {
    if (!persistId) return;
    try {
      await deleteRecording(persistId);
    } catch (e) {
      console.warn('[recording] 임시 보관 삭제 실패', e);
    }
    releasePersistLock(persistId);
  }, [releasePersistLock]);

  const flushPersist = useCallback(async (session) => {
    const persist = session?.persist;
    if (!persist || persist.closed || persist.broken || persist.flushing) return;
    persist.flushing = true;
    try {
      // finalize 뒤엔 finalize 의 patch 가 최종값이다(업로드가 비운 제목으로 덮어쓰지 않게).
      if (!session.finalized) {
        const title = recTitleRef.current || '';
        const notes = session.notes || [];
        if (title !== persist.lastTitle || notes !== persist.lastNotes) {
          persist.lastTitle = title;
          persist.lastNotes = notes;
          void patchSession(persist.id, { title, notes: [...notes] }).catch(() => {});
        }
      }
      while (persist.nextSeq < session.chunks.length && !persist.closed && !persist.broken) {
        const seq = persist.nextSeq;
        let stored = false;
        try {
          // 이어 녹음 구간은 앞 구간 조각 번호 뒤(seqBase)부터 쌓는다 — 새 녹음은 seqBase 0 이라 종전과 같다.
          stored = await putChunk(persist.id, (persist.seqBase || 0) + seq, session.chunks[seq]);
        } catch (e) {
          // 저장 공간 부족 등 — 이 녹음의 보관만 멈춘다. 🔴 녹음 자체는 멈추지 않는다.
          persist.broken = true;
          void patchSession(persist.id, { partial: true }).catch(() => {});
          if (!persistWarnedRef.current) {
            persistWarnedRef.current = true;
            void alertDialog('브라우저 저장 공간이 부족해 새로고침 대비 임시 보관을 멈췄습니다. 녹음은 계속됩니다 — 마치면 바로 업로드해 주세요.');
          }
          break;
        }
        if (!stored) {
          persist.closed = true;   // 레코드가 이미 없다(업로드 성공·사용자 삭제)
          break;
        }
        persist.nextSeq = seq + 1;
      }
    } catch (e) {
      persist.broken = true;
    } finally {
      // 🔴 루프 끝과 이 줄 사이에 await 를 두지 않는다 — 그 사이에 온 마지막 조각을 놓친다.
      persist.flushing = false;
    }
  }, []);

  // 🔴 녹음은 이걸 기다리지 않는다. 준비가 끝나면 그때까지 쌓인 조각을 따라잡아 저장한다.
  const setupPersist = useCallback(async (session) => {
    if (!session || !isRecordingPersistSupported()) return;
    const ownerUserId = currentUserId();
    if (!ownerUserId) return;
    const persistId = newPersistId();
    const release = await acquireRecLock(persistId);
    if (!release) return;
    // 🔴 이 확인은 잠금을 맵에 넣기 **전**의 await 뒤에만 한다. 넣은 뒤에 풀면 미리보기·업로드가
    //    쥐고 있어야 할 잠금이 풀려 다른 탭이 같은 녹음을 복구한다(중복 업로드).
    const abandoned = () => session.finalized || session.cancelled || recSessionRef.current !== session;
    if (abandoned()) { try { release(); } catch {} return; }
    let stored = false;
    // 보관 준비 중(putSession 왕복)에 녹음 바에서 언어를 바꾸면 session.meta 가 새 객체로 바뀐다
    // (setRecLanguage) — 그때는 session.persist 가 아직 없어 보관 행을 못 고쳤으니 아래에서 맞춘다.
    const metaAtPut = session.meta;
    try {
      stored = await putSession({
        persistId,
        ownerUserId,
        startedAt: session.startedAt,
        updatedAt: Date.now(),
        state: 'recording',
        title: '',
        notes: [],
        meta: metaAtPut,
        mime: session.mr?.mimeType || session.mime || 'audio/webm',
        partial: false,
      });
    } catch (e) {
      stored = false;
    }
    if (!stored || abandoned()) { try { release(); } catch {} return; }
    persistLocksRef.current.set(persistId, release);
    session.persist = { id: persistId, nextSeq: 0, closed: false, broken: false, flushing: false, lastTitle: '', lastNotes: null };
    if (session.meta !== metaAtPut) void patchSession(persistId, { meta: session.meta }).catch(() => {});
    void flushPersist(session);
  }, [currentUserId, flushPersist]);

  // 이어 녹음 구간 보관. 🔴 경계 기록이 먼저, 보관은 그 뒤 — 경계 patch 가 성공(=== true)한 뒤에만
  //    session.persist 를 세운다. 실패하면 이 구간은 보관하지 않는다(녹음은 계속 · 이전 구간 행은 그대로).
  //    경계를 잃은 조각이 이전 구간에 붙어 앞 녹음이 소리 없이 사라지는 일을 막는다.
  //    잠금은 미리보기 때부터 이 탭이 쥐고 있다(setupPersist 처럼 새로 잡지 않는다).
  const setupResumePersist = useCallback(async (session) => {
    const resume = session?.resume;
    if (!resume) return;
    const abandoned = () => (
      session.finalized || session.cancelled || resume.discarded || recSessionRef.current !== session
    );
    const warn = () => { if (!abandoned()) void alertDialog(RESUME_NO_PERSIST_MESSAGE); };
    if (resume.seqBase == null || !isRecordingPersistSupported()) { warn(); return; }
    const mime = session.mr?.mimeType || session.mime || 'audio/webm';
    // «이전» = 정규화한 목록(옛 행은 [{startSeq:0, mime:row.mime}]) — 미리보기를 만들 때 구간에서 뽑아 뒀다.
    const segments = [...resume.persistSegments, { startSeq: resume.seqBase, mime }];
    let ok = false;
    try {
      ok = (await patchSession(resume.persistId, { segments, state: 'recording' })) === true;
    } catch (e) {
      ok = false;
    }
    if (ok) resume.persistSegments = segments;
    if (abandoned()) return;
    if (!ok) { warn(); return; }
    session.persist = {
      id: resume.persistId,
      nextSeq: 0,
      seqBase: resume.seqBase,
      closed: false,
      broken: false,
      flushing: false,
      lastTitle: '',
      lastNotes: null,
    };
    void flushPersist(session);
  }, [flushPersist]);

  const minimizeRecordingScreen = useCallback(() => {
    setRecordingScreenMinimized(true);
  }, []);

  const restoreRecordingScreen = useCallback(() => {
    setRecordingScreenMinimized(false);
  }, []);

  const syncRecTimingRefs = useCallback((session) => {
    recStartRef.current = session?.startedAt || 0;
    recPausedMsRef.current = session?.pausedMs || 0;
    recPauseStartedRef.current = session?.pauseStartedAt || 0;
    recPausedRef.current = !!session?.paused;
  }, []);

  const cleanupRecMeter = useCallback((resetLevel = true) => {
    if (recLevelRafRef.current) {
      cancelAnimationFrame(recLevelRafRef.current);
      recLevelRafRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    try { recSourceRef.current?.disconnect(); } catch {}
    try { recAnalyserRef.current?.disconnect(); } catch {}
    recSourceRef.current = null;
    recAnalyserRef.current = null;
    recLevelDataRef.current = null;
    const ctx = recAudioCtxRef.current;
    recAudioCtxRef.current = null;
    try { if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {}); } catch {}
    if (resetLevel) setRecLevel(0);
    // 미터가 죽으면 무음 판정도 같이 멈춘다(경고가 화면에 남지 않게).
    lastSoundAtRef.current = 0;
    silenceWarnRef.current = false;
    silenceWarnStartedRef.current = 0;
    setSilenceWarnSec(null);
  }, []);

  const clearRecPreview = useCallback(() => {
    const preview = recPreviewRef.current;
    if (preview?.url) URL.revokeObjectURL(preview.url);
    recPreviewRef.current = null;
    setRecPreview(null);
    if (!pendingUploadsRef.current.some((item) => item.status === 'failed')) setFloatingError('');
  }, []);

  const getRecordingAudioMs = useCallback((now = Date.now()) => {
    const session = recSessionRef.current;
    const startedAt = session?.startedAt || recStartRef.current;
    if (!startedAt) return 0;
    const pausedMs = session ? session.pausedMs : recPausedMsRef.current;
    const paused = session ? session.paused : recPausedRef.current;
    const pauseStartedAt = session ? session.pauseStartedAt : recPauseStartedRef.current;
    const openPauseMs = paused && pauseStartedAt ? now - pauseStartedAt : 0;
    // 이어 녹음은 앞 구간 길이(offsetMs)를 더한다 — 경과 표시·메모 시각·30분 확인이 전체 녹음 기준이 된다.
    const offsetMs = session ? (session.offsetMs || 0) : 0;
    return Math.max(0, now - startedAt - pausedMs - openPauseMs) + offsetMs;
  }, []);

  const getRecordingAudioSec = useCallback((now = Date.now()) => (
    Math.round((getRecordingAudioMs(now) / 1000) * 100) / 100
  ), [getRecordingAudioMs]);

  const closeOpenRecPause = useCallback((session = recSessionRef.current, now = Date.now()) => {
    if (!session) return;
    if (session.paused && session.pauseStartedAt) {
      session.pausedMs += Math.max(0, now - session.pauseStartedAt);
    }
    session.pauseStartedAt = 0;
    session.paused = false;
    if (recSessionRef.current?.id === session.id) {
      syncRecTimingRefs(session);
      setRecPaused(false);
    }
  }, [syncRecTimingRefs]);

  const syncPendingUploads = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(pendingUploadsRef.current) : updater;
    pendingUploadsRef.current = next;
    setPendingUploads(next);
    return next;
  }, []);

  const updatePendingUpload = useCallback((id, patch) => {
    syncPendingUploads((current) => current.map((item) => (
      item.id === id
        ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch) }
        : item
    )));
  }, [syncPendingUploads]);

  const clearPendingUploadRetry = useCallback((id) => {
    const timer = pendingUploadRetryTimersRef.current[id];
    if (timer) clearTimeout(timer);
    delete pendingUploadRetryTimersRef.current[id];
  }, []);

  const clearAllPendingUploadRetries = useCallback(() => {
    Object.values(pendingUploadRetryTimersRef.current).forEach((timer) => clearTimeout(timer));
    pendingUploadRetryTimersRef.current = {};
  }, []);

  const requestUploadWakeLock = useCallback(async () => {
    try {
      if (typeof navigator === 'undefined' || !navigator.wakeLock?.request) return null;
      return await navigator.wakeLock.request('screen');
    } catch {
      return null;
    }
  }, []);

  const releaseUploadWakeLock = useCallback((wakeLock) => {
    try { wakeLock?.release?.(); } catch {}
  }, []);

  const removePendingUpload = useCallback((id) => {
    clearPendingUploadRetry(id);
    delete pendingUploadRunsRef.current[id];
    const next = syncPendingUploads((current) => current.filter((item) => item.id !== id));
    if (!next.some((item) => item.status === 'failed')) setFloatingError('');
    return next;
  }, [clearPendingUploadRetry, syncPendingUploads]);

  const uploadPendingItem = useCallback(async (upload, options = {}) => {
    clearPendingUploadRetry(upload.id);
    const retryAttempt = Number(options.retryAttempt || 0);
    const runId = (pendingUploadRunsRef.current[upload.id] || 0) + 1;
    pendingUploadRunsRef.current[upload.id] = runId;
    const isCurrentRun = () => pendingUploadRunsRef.current[upload.id] === runId;
    const meta = upload.meta || {};
    // 이어 녹음 구간 — 2개 이상일 때만 `segments` 파트를 붙인다(file = 첫 구간). 1개면 종전 요청 그대로.
    const segments = Array.isArray(upload.segments) ? upload.segments.filter((seg) => seg && seg.blob) : [];
    const multiSegment = segments.length >= 2;
    const fd = new FormData();
    fd.append('file', multiSegment ? segments[0].blob : upload.blob, upload.filename);
    if (multiSegment) {
      for (let k = 1; k < segments.length; k += 1) {
        fd.append('segments', segments[k].blob, `segment_${k}.${extForMime(segments[k].mime || segments[k].blob.type)}`);
      }
    }
    if (meta.folderId) fd.append('folder_id', meta.folderId);
    fd.append('title', upload.title);
    fd.append('source', upload.source || 'record');
    if (Array.isArray(upload.notes)) {
      fd.append('recording_notes', JSON.stringify(upload.notes));
    }
    fd.append('diarize', meta.diarize ? 'true' : 'false');
    // 참석자·인식 언어는 **값이 있을 때만** 붙인다 — 안 붙이면 서버가 '안 보낸 것'으로 보고
    // NULL 을 유지한다(빈 배열·빈 문자열을 보내는 것과 결과는 같지만 요청이 종전과 같아진다).
    if (Array.isArray(meta.participants) && meta.participants.length) {
      fd.append('participants', JSON.stringify(meta.participants));
    }
    if (typeof meta.language === 'string' && meta.language) fd.append('language', meta.language);
    updatePendingUpload(upload.id, { status: 'uploading', pct: 0 });
    if (upload.persistId) void patchSession(upload.persistId, { state: 'uploading' }).catch(() => {});
    const wakeLock = await requestUploadWakeLock();
    try {
      const res = await meetingAPI.upload(fd, (e) => {
        if (isCurrentRun() && e.total) {
          updatePendingUpload(upload.id, { pct: Math.round((e.loaded / e.total) * 100) });
        }
      });
      // 🔴 응답 확인은 구간 2개 이상을 보낸 업로드에만 한다 — 구간 1개(지금의 모든 녹음)는 종전처럼 바로 지운다.
      //    구간 2개 이상은 서버가 받은 구간 수(segments_received = file + segments)가 보낸 수와 같을 때만 지운다.
      const data = res?.data || {};
      const segmentsOk = !multiSegment || Number(data.segments_received) === segments.length;
      if (segmentsOk) {
        // 🔴 서버에 저장됐으면 run 이 무효화됐어도(forceClear 뒤 늦게 성공) 로컬 사본은 지운다.
        if (upload.persistId) void dropPersisted(upload.persistId);
      } else {
        // 서버에는 첫 구간만 담긴 노트가 이미 생겼다 — 대기열 항목은 성공으로 빼고(재시도하면 노트가
        // 중복된다) 잠금만 푼다. 보관 행은 남긴다(다음 로드에 다시 제안 · 관리자 확인용).
        if (upload.persistId) releasePersistLock(upload.persistId);
        void alertDialog(SEGMENTS_NOT_RECEIVED_MESSAGE);
      }
      const dropped = Number(data.segments_dropped) || 0;
      if (multiSegment && dropped > 0) void alertDialog(`녹음 구간 ${dropped}개를 읽지 못해 빼고 저장했습니다`);
      if (!isCurrentRun()) return true;
      removePendingUpload(upload.id);
      setUploadVersion((v) => v + 1);
      return true;
    } catch (err) {
      if (upload.persistId) {
        // forceClear 로 대기열에서 빠진 항목이면 레코드는 남기고(다음 스캔이 제안) 잠금만 푼다.
        const stillQueued = pendingUploadsRef.current.some((item) => item.id === upload.id);
        void patchSession(upload.persistId, { state: 'stopped' }).catch(() => {});
        if (!stillQueued) releasePersistLock(upload.persistId);
      }
      if (!isCurrentRun()) return false;
      const message = uploadFailureMessage(err, multiSegment);
      // 422 = 이어붙이기 실패(다시 해도 실패) — 그 항목의 [삭제]는 모든 구간이 지워진다고 묻는다.
      const mergeFailed = multiSegment && err?.response?.status === 422;
      updatePendingUpload(upload.id, mergeFailed ? { status: 'failed', mergeFailed: true } : { status: 'failed' });
      setFloatingError(message);
      if (retryAttempt < 1 && shouldAutoRetryUpload(err)) {
        pendingUploadRetryTimersRef.current[upload.id] = setTimeout(() => {
          delete pendingUploadRetryTimersRef.current[upload.id];
          if (!isCurrentRun()) return;
          const current = pendingUploadsRef.current.find((item) => item.id === upload.id);
          if (!current || current.status !== 'failed') return;
          updatePendingUpload(upload.id, { status: 'uploading', pct: 0 });
          setFloatingError((currentMessage) => (currentMessage === message ? '' : currentMessage));
          void uploadPendingItemRef.current?.({ ...current, status: 'uploading', pct: 0 }, { retryAttempt: retryAttempt + 1 });
        }, UPLOAD_AUTO_RETRY_DELAY_MS);
      }
      return false;
    } finally {
      releaseUploadWakeLock(wakeLock);
    }
  }, [clearPendingUploadRetry, dropPersisted, releasePersistLock, releaseUploadWakeLock, removePendingUpload, requestUploadWakeLock, updatePendingUpload]);
  uploadPendingItemRef.current = uploadPendingItem;

  const enqueueUpload = useCallback((payload) => {
    if (pendingUploadsRef.current.length >= MAX_PENDING_UPLOADS) {
      alertDialog('진행 중인 녹음 업로드가 많습니다. 이전 업로드를 정리한 후 다시 시도해 주세요.');
      return null;
    }
    const id = pendingUploadSeqRef.current + 1;
    pendingUploadSeqRef.current = id;
    const item = {
      id,
      blob: payload.blob,
      filename: payload.filename,
      title: payload.title,
      notes: Array.isArray(payload.notes) ? [...payload.notes] : [],
      meta: payload.meta ? { ...payload.meta } : {},
      source: payload.source || 'record',
      persistId: payload.persistId || null,
      // 이어 녹음 구간 [{blob, mime}] — 없으면 null(종전과 같다).
      segments: Array.isArray(payload.segments) ? [...payload.segments] : null,
      status: 'uploading',
      pct: 0,
    };
    syncPendingUploads((current) => [...current, item]);
    void uploadPendingItem(item);
    return item;
  }, [syncPendingUploads, uploadPendingItem]);

  const retryPendingUpload = useCallback((id) => {
    const targetId = id ?? pendingUploadsRef.current.find((upload) => upload.status === 'failed')?.id;
    const item = pendingUploadsRef.current.find((upload) => upload.id === targetId);
    if (!item || item.status !== 'failed') return false;
    const otherFailed = pendingUploadsRef.current.some((upload) => upload.id !== targetId && upload.status === 'failed');
    updatePendingUpload(targetId, { status: 'uploading', pct: 0 });
    setFloatingError(otherFailed ? '업로드 실패 — 재시도하세요' : '');
    void uploadPendingItem({ ...item, status: 'uploading', pct: 0 });
    return true;
  }, [updatePendingUpload, uploadPendingItem]);

  const discardPendingUpload = useCallback(async (id) => {
    const targetId = id ?? pendingUploadsRef.current.find((upload) => upload.status === 'failed')?.id;
    const item = pendingUploadsRef.current.find((upload) => upload.id === targetId);
    if (!item || item.status !== 'failed') return false;
    if (!(await confirmDialog(item.mergeFailed ? MERGE_FAILED_DISCARD_CONFIRM : '실패한 녹음 업로드를 삭제할까요?'))) return false;
    if (item.persistId) void dropPersisted(item.persistId);
    removePendingUpload(targetId);
    return true;
  }, [dropPersisted, removePendingUpload]);

  const clearPendingUploads = useCallback(() => {
    clearAllPendingUploadRetries();
    pendingUploadRunsRef.current = {};
    syncPendingUploads([]);
  }, [clearAllPendingUploadRetries, syncPendingUploads]);

  const resetCurrentRecSession = useCallback(({ keepDraft = false } = {}) => {
    recSessionRef.current = null;
    mediaRef.current = null;
    recStartRef.current = 0;
    recPausedMsRef.current = 0;
    recPauseStartedRef.current = 0;
    recPausedRef.current = false;
    setRecording(false);
    setRecPaused(false);
    setRecPauseSupported(false);
    if (!keepDraft) {
      setRecordingNotes([]);
      updateRecTitle('');
      setElapsedSec(0);
    }
    setRecLevel(0);
    lastSoundAtRef.current = 0;
    silenceWarnRef.current = false;
    silenceWarnStartedRef.current = 0;
    setSilenceWarnSec(null);
    heardSoundRef.current = false;
    meterDeadSinceRef.current = 0;
    meterRetryRef.current = 0;
    recMeterStreamRef.current = null;
    meterFailedRef.current = false;
    noSoundWarnRef.current = false;
    setMeterFailed(false);
    setNoSoundWarn(false);
    // 30분 확인 초기화 — 🔴 미확인 표시와 다음 시점을 **함께** 되돌리는 곳은 녹음 세션이 바뀌는
    //    여기와 startRec 두 곳뿐이다. 녹음 도중에 불리는 자리(미터 재시도·무음 판정·«계속 녹음»)에서
    //    되돌리면 확인한 지 몇 분 만에 다시 묻거나, 아직 답하지 않은 확인이 사라진다.
    setAliveCheckMinLocked(null);
    aliveNextMsRef.current = ALIVE_CHECK_MS;
    closeAliveNotifications();
  }, [closeAliveNotifications, setAliveCheckMinLocked, updateRecTitle]);

  const startRecMeter = useCallback((stream, { isRetry = false } = {}) => {
    cleanupRecMeter();
    recMeterStreamRef.current = stream || recMeterStreamRef.current;
    meterDeadSinceRef.current = 0;
    if (!isRetry) {
      // 새 녹음 시작 — 가드 상태를 여기서 초기화한다(resetCurrentRecSession 은 시작 시점엔 안 불린다).
      // ⚠️ 재시도(isRetry)일 땐 건드리면 안 된다. 시도 횟수가 리셋돼 무한 재시도가 된다.
      heardSoundRef.current = false;
      meterRetryRef.current = 0;
      noSoundWarnRef.current = false;
      setNoSoundWarn(false);
    }
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      // 미터를 못 만들면 무음 자동중지가 아예 없다 — 조용히 넘어가지 않고 알린다.
      meterFailedRef.current = true;
      setMeterFailed(true);
      return;
    }
    try {
      const ctx = new AudioContextCtor();
      const analyser = ctx.createAnalyser();
      const source = ctx.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      source.connect(analyser);
      recAudioCtxRef.current = ctx;
      recAnalyserRef.current = analyser;
      recSourceRef.current = source;
      recLevelDataRef.current = new Uint8Array(analyser.fftSize);
      lastSoundAtRef.current = Date.now();   // 시작 시점을 기준으로 무음 시간을 센다
      silenceWarnRef.current = false;
      silenceWarnStartedRef.current = 0;
      setSilenceWarnSec(null);
      if (meterFailedRef.current) { meterFailedRef.current = false; setMeterFailed(false); }
      ctx.resume?.().catch(() => {});

      const readLevel = () => {
        const an = recAnalyserRef.current;
        const buf = recLevelDataRef.current;
        if (!an || !buf) return null;
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        return Math.min(1, Math.sqrt(sum / buf.length) * 4.5);
      };

      // 화면 표시용 레벨 미터(부드럽게). 탭이 가려지면 브라우저가 rAF 를 멈추는데,
      // 안 보이는 동안의 막대는 어차피 의미가 없으므로 그대로 둔다.
      const tick = () => {
        if (recPausedRef.current) setRecLevel(0);
        else {
          const level = readLevel();
          if (level == null) return;
          setRecLevel(level < 0.015 ? 0 : level);
        }
        recLevelRafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // 무음 판정은 rAF 가 아니라 타이머로 돈다. 🔴 자리를 비우면 탭이 뒤로 가고
      // 그 순간 rAF 가 멈춘다 — 정작 잡아야 할 상황에서 감지가 죽어버린다.
      // 타이머도 숨은 탭에서 느려지지만(최소 1초, 길게는 1분) 임계값이 분 단위라 문제없다.
      const checkSilence = () => {
        if (recPausedRef.current) {
          // 일시정지 동안은 무음 시계를 세운다. 안 그러면 10분 멈췄다 재개하는 순간 경고가 뜬다.
          lastSoundAtRef.current = Date.now();
          if (silenceWarnRef.current) {
            silenceWarnRef.current = false;
            silenceWarnStartedRef.current = 0;
            setSilenceWarnSec(null);
          }
          return;
        }
        const level = readLevel();
        const now = Date.now();
        if (level == null) {
          // 🔴 예전엔 그냥 return 이라 미터가 죽으면 무음 가드가 통째로 사라졌다(2026-08-18 사고).
          //    되살려 보고, 그래도 안 되면 "자동중지가 동작하지 않는다"를 사용자에게 알린다.
          //    (레벨을 모르는 상태에서 임의로 자동중지하면 멀쩡한 회의 녹음을 끊을 수 있다.)
          if (!meterDeadSinceRef.current) meterDeadSinceRef.current = now;
          if (now - meterDeadSinceRef.current >= METER_DEAD_MS) {
            if (meterRetryRef.current < METER_RETRY_MAX && recMeterStreamRef.current) {
              meterRetryRef.current += 1;
              startRecMeterRef.current?.(recMeterStreamRef.current, { isRetry: true });
            } else if (!meterFailedRef.current) {
              meterFailedRef.current = true;
              setMeterFailed(true);
            }
          }
          return;
        }
        meterDeadSinceRef.current = 0;
        if (meterFailedRef.current) { meterFailedRef.current = false; setMeterFailed(false); }
        if (level >= SILENCE_LEVEL) {
          lastSoundAtRef.current = now;
          heardSoundRef.current = true;
          if (noSoundWarnRef.current) { noSoundWarnRef.current = false; setNoSoundWarn(false); }
          if (silenceWarnRef.current) {
            silenceWarnRef.current = false;
            silenceWarnStartedRef.current = 0;
            setSilenceWarnSec(null);
          }
          return;
        }
        if (!lastSoundAtRef.current) return;
        // 시작부터 한 번도 소리가 없으면 7분을 기다리지 않고 바로 알린다 —
        // 무음인 줄 모르고 52분을 녹음한 사고가 실제로 있었다(자동중지는 종전대로 7분 뒤).
        if (!heardSoundRef.current && !noSoundWarnRef.current
            && now - lastSoundAtRef.current >= NO_SOUND_EARLY_MS) {
          noSoundWarnRef.current = true;
          setNoSoundWarn(true);
        }
        if (!silenceWarnRef.current) {
          if (now - lastSoundAtRef.current >= SILENCE_PROMPT_MS) {
            silenceWarnRef.current = true;
            silenceWarnStartedRef.current = now;
            setSilenceWarnSec(Math.ceil(SILENCE_AUTOSTOP_MS / 1000));
          }
          return;
        }
        const leftMs = SILENCE_AUTOSTOP_MS - (now - silenceWarnStartedRef.current);
        if (leftMs > 0) {
          setSilenceWarnSec((prev) => {
            const next = Math.ceil(leftMs / 1000);
            return prev === next ? prev : next;   // 초가 바뀔 때만 렌더
          });
          return;
        }
        silenceWarnRef.current = false;
        silenceWarnStartedRef.current = 0;
        lastSoundAtRef.current = 0;      // 중복 발동 방지
        setSilenceWarnSec(null);
        // 자리를 비운 상태에서 멈추는 것이라 미리듣기로 세워두면 안 된다
        // (탭을 닫으면 그대로 날아간다) — 바로 업로드 큐에 넣는다.
        stopRecRef.current?.({ autoUpload: true });
      };
      silenceTimerRef.current = setInterval(checkSilence, SILENCE_CHECK_MS);
    } catch {
      // 🔴 여기서 조용히 끝내면 녹음은 계속되는데 무음 가드만 사라진다(2026-08-18 사고).
      cleanupRecMeter();
      meterFailedRef.current = true;
      setMeterFailed(true);
    }
  }, [cleanupRecMeter]);
  startRecMeterRef.current = startRecMeter;   // checkSilence 안에서 미터를 되살릴 때 쓴다

  // 이어 녹음 중 [취소] 뒤 — 이어 녹음 전 구간을 미리보기로 되살린다.
  // 🔴 취소 finalize 의 resetCurrentRecSession **뒤**에 부른다(그 전에 세우면 reset 이 제목·메모를 지운다).
  //    옛 미리보기 URL 은 이미 해제됐으니 복구 스캔과 같은 방식(readChunkEntries + splitSegments)으로 새로 만든다.
  const reviveResumePreview = useCallback(async (resume) => {
    const { persistId } = resume;
    try { await resume.discardPromise; } catch {}
    if (!persistLocksRef.current.has(persistId)) return;
    let row = null;
    let entries = [];
    try {
      const rows = await listSessions();
      row = (rows || []).find((item) => item?.persistId === persistId) || null;
      if (row) entries = await readChunkEntries(persistId);
    } catch (e) {
      row = null;
    }
    if (!persistLocksRef.current.has(persistId)) return;
    // 지우기가 실패했어도 새 구간 조각은 되살리지 않는다(seqBase 이후 = 이어 녹음 구간).
    const kept = resume.seqBase == null ? entries : entries.filter((item) => Number(item?.seq) < resume.seqBase);
    const split = row ? splitSegments(row, kept) : [];
    if (!split.length || !isRecordingIdle()) {
      // 되살릴 수 없거나 그 사이 다른 녹음이 시작됐다 — 행은 남기고 잠금만 푼다(다음 로드에 다시 묻는다).
      releasePersistLock(persistId);
      return;
    }
    const preview = buildRecoveredPreview(row, split, persistId, { title: resume.title, notes: resume.notes });
    recPreviewRef.current = preview;
    setRecPreview(preview);
    updateRecTitle(preview.title);
    setRecordingNotes(preview.notes);
  }, [isRecordingIdle, releasePersistLock, updateRecTitle]);

  const finalizeRecSession = useCallback(async (session) => {
    if (!session || session.finalized) return;
    session.finalized = true;
    const isCurrent = recSessionRef.current?.id === session.id;
    let preview = null;
    if (isCurrent && !recFinalizingRef.current) setRecFinalizingLocked(true);
    closeOpenRecPause(session);
    if (isCurrent) {
      cleanupRecMeter();
      setRecording(false);
      setRecPauseSupported(false);
      setRecPaused(false);
    }
    try { stopRecordingStream(session.stream); } catch {}
    try {
      if (!session.cancelled) {
        const type = session.mr?.mimeType || session.mime || 'audio/webm';
        const t = type.toLowerCase();
        const ext = t.includes('mp4') ? 'm4a' : t.includes('aac') ? 'aac' : t.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(session.chunks, { type });
        const title = (session.title || '').trim() || ('녹음 ' + new Date().toLocaleString('ko-KR'));
        const now = Date.now();
        const durationSec = Math.max(0, Math.round(((now - session.startedAt - session.pausedMs) / 1000) * 100) / 100);
        preview = {
          blob,
          url: null,
          filename: `recording_${now}.${ext}`,
          title,
          notes: [...(session.notes || [])],
          durationSec,
        };
        // 이어 녹음 — 앞 구간 + 새 구간(조각 0개면 뺀다). blob = 첫 구간 · 길이 = 합계 · 복구물 표시를 잇는다.
        if (Array.isArray(session.prevSegments)) {
          const segments = [...session.prevSegments];
          if (session.chunks.length) segments.push({ blob, mime: type, chunkCount: session.chunks.length });
          if (segments.length) {
            preview.blob = segments[0].blob;
            preview.filename = `recording_${now}.${extForMime(segments[0].mime)}`;
            preview.segments = segments;
          }
          preview.durationSec = Math.round(((session.offsetMs || 0) / 1000 + durationSec) * 100) / 100;
          preview.recovered = true;
          preview.resumable = true;
          preview.persistSegments = [...(session.resume?.persistSegments || [])];
        }
        // 🔴 이어 녹음은 경계 기록이 실패해 session.persist 가 없어도 그 보관 행(resume)을 가리킨다 —
        //    업로드 성공 때 지워지고 잠금이 풀려야 다음 로드에 같은 녹음을 또 제안하지 않는다(중복 노트).
        const persistId = session.persist?.id || session.resume?.persistId || null;
        if (persistId) {
          preview.persistId = persistId;
          // 🔴 여기서 레코드를 지우지 않는다 — 업로드 성공·사용자 삭제 때만 지운다.
          void patchSession(persistId, { state: 'stopped', title, notes: preview.notes }).catch(() => {});
        }
        if (session.autoUpload) {
          const queued = enqueueUpload({ blob: preview.blob, filename: preview.filename, title, notes: preview.notes, meta: session.meta, source: 'record', persistId, segments: preview.segments || null });
          if (queued) preview = null;
          else preview.url = URL.createObjectURL(preview.blob);
        } else {
          preview.url = URL.createObjectURL(preview.blob);
        }
        if (preview && isCurrent) {
          clearRecPreview();
          recPreviewRef.current = preview;
          setRecPreview(preview);
          updateRecTitle(title);
          setRecordingNotes(preview.notes);
        } else if (preview) {
          // 이 탭이 더는 들고 있지 않은 미리보기 — 레코드는 남기고 잠금만 푼다(다음 스캔이 제안).
          if (preview.url) URL.revokeObjectURL(preview.url);
          if (persistId) releasePersistLock(persistId);
        }
      }
    } finally {
      if (isCurrent) resetCurrentRecSession({ keepDraft: !!preview });
      if (isCurrent || !recSessionRef.current) setRecFinalizingLocked(false);
    }
    // 이어 녹음 중 [취소] — 새 구간만 버렸으니 앞 구간을 미리보기로 되살린다(reset 뒤).
    if (isCurrent && session.cancelled && session.resume?.discarded) void reviveResumePreview(session.resume);
  }, [clearRecPreview, cleanupRecMeter, closeOpenRecPause, enqueueUpload, releasePersistLock, resetCurrentRecSession, reviveResumePreview, setRecFinalizingLocked, updateRecTitle]);

  const pickRecMime = useCallback(() => {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg'];
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
      for (const c of cands) { if (MediaRecorder.isTypeSupported(c)) return c; }
    }
    return '';
  }, []);

  const addRecordingNote = useCallback((text = '') => {
    const session = recSessionRef.current;
    if (!recording || !session || recFinalizingRef.current || session.notes.length >= 200) return null;
    const memo = (text || '').trim();
    const note = {
      time: getRecordingAudioSec(),
      text: memo.slice(0, 1000),
    };
    const next = [...session.notes, note].slice(0, 200);
    session.notes = next;
    setRecordingNotes(next);
    return next.length - 1;
  }, [getRecordingAudioSec, recording]);

  const removeRecordingNote = useCallback((idx) => {
    const session = recSessionRef.current;
    if (!session) return;
    const next = session.notes.filter((_, i) => i !== idx);
    session.notes = next;
    setRecordingNotes(next);
  }, []);

  // 녹음 바 «언어» — 녹음 중에만 인식 언어를 바꾼다(미리듣기·녹음 전·마무리 중은 false).
  // 🔴 의존 [] — 위 «헬퍼들도 ref·모듈 함수만 쓴다» 규칙(identity 가 바뀌면 녹음이 끊긴다).
  const setRecLanguage = useCallback((value) => {
    const code = cleanRecLanguage(value);
    if (!code) return false;   // 목록 밖 값은 버린다(서버는 400)
    const session = recSessionRef.current;
    if (!session || session.finalized || session.cancelled || recFinalizingRef.current) return false;
    // 🔴 객체를 **새로** 만든다 — setupPersist 가 `session.meta !== metaAtPut` 로 «보관 준비 중에 바뀌었나»를 가린다.
    //    (큐에 들어간 업로드는 enqueueUpload 의 얕은 복사로 이미 분리돼 있다 — language 는 원시값이다.)
    const next = { ...session.meta, language: code };
    // 🔴 두 자리를 같이 바꾼다 — 자동 업로드는 session.meta, 미리듣기 업로드는 recMetaRef.current 를 읽는다.
    recMetaRef.current = next;
    session.meta = next;
    setRecLanguageState(code);
    const persist = session.persist;
    if (persist && !persist.closed) void patchSession(persist.id, { meta: next }).catch(() => {});
    return true;
  }, []);

  const startRec = useCallback(async (meta = {}, options = {}) => {
    // 이어 녹음(continueRecoveredRec)은 그 복구 미리보기가 떠 있는 채로 들어온다 — 그 미리보기일 때만 허용한다.
    const resume = options?.resume || null;
    if (recording || recStartingRef.current || recFinalizingRef.current || recSessionRef.current) return;
    if (resume ? recPreviewRef.current !== resume.pv : recPreviewRef.current) return;
    let stream = null;
    let session = null;
    // «PC 소리도 함께 녹음»: pc = acquirePcAudio 결과 · mic = 섞기 전 마이크 스트림 · mixedOk = 섞은 스트림으로 녹음하나.
    let pc = null;
    let mic = null;
    let mixedOk = false;
    if (!pendingUploadsRef.current.some((item) => item.status === 'failed')) setFloatingError('');
    setRecStartingLocked(true);
    // 🔴 이어 녹음은 새 녹음의 setupPersist 보호(abandoned)를 건너뛰므로 await 마다 여기서 대신 본다 —
    //    미리보기가 바뀌었거나 잠금이 사라졌으면(로그아웃 감시의 forceClearRecording 등) 스트림을 놓고 끝낸다.
    const resumeLost = () => !!resume && !(
      recStartingRef.current
      && recPreviewRef.current === resume.pv
      && persistLocksRef.current.has(resume.persistId)
    );
    try {
      // «PC 소리도 함께 녹음»(새 노트 화면 · 브라우저 선호값)이면 PC 소리를 **마이크보다 먼저** 받는다 —
      // 화면 공유 창은 사용자 클릭 직후에만 뜬다. 🔴 꺼짐이면 선호값을 한 번 읽는 것 말고 아무것도 하지 않는다.
      const pcPref = readPcAudioPref();
      if (pcPref.mode !== 'off') {
        pc = await acquirePcAudio(pcPref);
        // 공유 창이 떠 있는 사이 로그아웃 감시가 시작 잠금을 풀었으면 녹음을 시작하지 않는다(finally 는 돈다).
        if (!recStartingRef.current || resumeLost()) { stopRecordingStream(pc?.stream); return; }
      }
      // «주변 소리 그대로 녹음»(새 노트 화면 · 브라우저 선호값)이면 잡음 억제·에코 제거·자동 음량을 끈다.
      stream = await navigator.mediaDevices.getUserMedia({ audio: recordingAudioConstraints() });
      if (resumeLost()) { stopRecordingStream(stream); stopRecordingStream(pc?.stream); return; }
      // 적용 확인 로그 — 켜도 TV 소리가 안 잡히면 «설정이 안 먹었다 / 다른 원인» 을 가른다.
      // 🔴 옵셔널 체이닝 필수(시험용 가짜 스트림은 getTracks 만 있다) · 로그 실패가 녹음을 막지 않게 한다.
      try {
        const st = stream?.getAudioTracks?.()?.[0]?.getSettings?.();
        if (st) console.info('[rec] audio settings', { noiseSuppression: st.noiseSuppression, echoCancellation: st.echoCancellation, autoGainControl: st.autoGainControl, raw: readRawAudioPref() });
      } catch {}
      // 고른 입력 장치가 지금 마이크와 같은 장치면 같은 소리가 두 번 섞인다 — PC 쪽을 놓고 마이크만 녹음한다(아래 안내).
      // 화면 공유(share)는 비교하지 않는다(탭·화면 소리라 마이크와 같은 장치일 수 없다). 판단 불가는 false = 종전처럼 섞는다.
      if (pc?.stream && pcPref.mode === 'device') {
        const same = await isSameInputDevice(stream, pcPref.deviceId);
        // 비교하는 사이 로그아웃 감시가 시작 잠금을 풀었으면 시작하지 않는다(finally 는 돈다).
        // 🔴 마이크도 이미 받았고 세션이 아직 없어 다른 정리 경로가 멈추지 않는다 — 두 스트림을 모두 놓는다.
        if (!recStartingRef.current || resumeLost()) { stopRecordingStream(stream); stopRecordingStream(pc.stream); return; }
        if (same) {
          stopRecordingStream(pc.stream);
          pc = { stream: null, reason: 'same-device' };
        }
      }
      // 이어 녹음 — 앞 구간의 마지막 조각 번호. 🔴 시작 잠금(setRecStartingLocked) 뒤에 읽는다(그 전에 기다리면
      // 그 사이 미리보기의 [업로드]·[취소]가 살아 있다). 못 읽으면 이 구간은 보관하지 않는다(녹음은 한다).
      if (resume) {
        let last = -1;
        try {
          last = await lastSeq(resume.persistId);
        } catch (e) {
          last = -1;
        }
        if (resumeLost()) { stopRecordingStream(stream); stopRecordingStream(pc?.stream); return; }
        resume.seqBase = Number.isFinite(Number(last)) && Number(last) >= 0 ? Number(last) + 1 : null;
      }
      // PC 소리를 받았으면 마이크와 섞은 스트림으로 녹음한다(MediaRecorder·음량 미터·session.stream 모두 이 스트림).
      // 섞기에 실패해도 마이크만으로 녹음을 시작한다(아래 안내).
      if (pc?.stream) {
        mic = stream;
        try {
          const mixed = mixStreams(mic, pc.stream);
          registerStreamCleanup(mixed.stream, mixed.cleanup);
          stream = mixed.stream;
          mixedOk = true;
        } catch (e) {
          stopRecordingStream(pc.stream);
          pc = { stream: null, reason: 'error', message: e?.message || '' };
        }
      }
      const mime = pickRecMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const startedAt = Date.now();
      // 🔴 meta.title 은 sessionMeta 에 넣지 않는다 — 제목의 정본은 recTitleRef 다(아래 updateRecTitle).
      const sessionMeta = cleanRecMeta(meta);
      recMetaRef.current = sessionMeta;
      setRecLanguageState(sessionMeta.language);
      // 이어 녹음 — 앞 구간 Blob 은 미리보기의 segments 를 그대로 들고 간다(다시 읽지 않는다).
      // offset = 앞 구간 조각 수 × 1초(경과 표시·메모 시각·30분 확인이 그만큼 밀린다).
      const prevSegments = resume ? resume.pv.segments.map((seg) => ({ ...seg })) : null;
      const offsetMs = resume
        ? estimateDurationSec(prevSegments.reduce((n, seg) => n + (Number(seg.chunkCount) || 0), 0)) * 1000
        : 0;
      session = {
        id: recSessionSeqRef.current + 1,
        stream,
        mr,
        mime,
        chunks: [],
        notes: resume ? [...resume.notes] : [],
        cancelled: false,
        finalized: false,
        autoUpload: false,
        title: '',
        startedAt,
        pausedMs: 0,
        pauseStartedAt: 0,
        paused: false,
        meta: sessionMeta,
        offsetMs,
        prevSegments,
        resume,
      };
      recSessionSeqRef.current = session.id;
      recSessionRef.current = session;
      if (mixedOk) {
        // 섞인 스트림의 트랙은 스스로 끝나지 않는다 — 원본(마이크·PC 소리)이 끊기는 것을 따로 지켜본다.
        // 🔴 종료 처리 중에는 조용히 · stopRecRef 는 인자 없이 = 자동 업로드 없이 미리듣기로 선다.
        const watchedSession = session;
        const micStream = mic;
        const pcStream = pc.stream;
        const watching = () => (
          recSessionRef.current?.id === watchedSession.id && !watchedSession.finalized && !recFinalizingRef.current
        );
        onTrackEnded(pcStream, () => {
          if (!watching()) return;
          if (hasLiveAudio(micStream)) void alertDialog('PC 소리 공유가 중지되었습니다. 마이크만 계속 녹음합니다.');
          else stopRecRef.current?.();
        });
        onTrackEnded(micStream, () => {
          if (!watching()) return;
          if (hasLiveAudio(pcStream)) void alertDialog('마이크 연결이 끊겼습니다. PC 소리만 계속 녹음합니다.');
          else stopRecRef.current?.();
        });
      }
      mediaRef.current = mr;
      setRecordingScreenMinimized(false);
      syncRecTimingRefs(session);
      // 이어 녹음은 미리보기의 메모·제목을 그대로 잇는다.
      setRecordingNotes(resume ? session.notes : []);
      // «새 노트»에서 제목을 적어 왔으면 그 제목으로 시작한다(없으면 종전처럼 빈 값 —
      // finalize 가 '녹음 <시각>' 으로 채운다). 길이는 제목 input maxLength 와 같게 자른다.
      updateRecTitle(resume
        ? resume.title
        : (typeof meta?.title === 'string' ? meta.title.trim().slice(0, REC_TITLE_MAX) : ''));
      setRecPaused(false);
      setRecPauseSupported(typeof mr.pause === 'function' && typeof mr.resume === 'function');
      setRecLevel(0);
      setElapsedSec(Math.floor(offsetMs / 1000));
      // 30분 확인 초기화(새 녹음) — resetCurrentRecSession 과 함께 이 두 곳에서만 한다.
      // 이어 녹음은 offset 이후 기준으로 다시 잡는다(이어 녹음 직후 바로 묻지 않게 · 새 녹음은 offset 0).
      setAliveCheckMinLocked(null);
      aliveNextMsRef.current = offsetMs + ALIVE_CHECK_MS;
      closeAliveNotifications();
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) {
          session.chunks.push(e.data);
          void flushPersist(session);
        }
      };
      mr.onstop = () => { finalizeRecSession(session); };
      startRecMeter(stream);
      mr.start(1000);
      if (resume) {
        // 🔴 mr.start 성공 뒤에 미리보기를 걷는다(잠금은 놓지 않는다 — 이 녹음이 계속 쥔다).
        //    그 전에 실패하면(마이크 거부·start 예외) 미리보기·잠금이 그대로 남는다.
        clearRecPreview();
        // 경계 기록 → 보관(기다리지 않는다 — 끝나면 그때까지 쌓인 조각을 따라잡는다).
        void setupResumePersist(session);
      } else {
        // 🔴 영속 준비를 기다리지 않는다 — 기다리면 녹음 시작이 늦어진다(끝나면 따라잡는다).
        void setupPersist(session);
      }
      setRecording(true);
      // PC 소리를 못 받아 마이크만으로 시작했으면 알린다(🔴 기다리지 않는다 — 녹음은 이미 돌고 있다).
      if (pc && !pc.stream) void alertDialog(pcAudioFallbackMessage(pc));
      if (resume) void alertDialog(RESUME_GAP_MESSAGE);
    } catch (e) {
      cleanupRecMeter();
      // 이어 녹음은 화면의 메모·제목을 비우지 않는다(미리보기가 그대로 남는다).
      if (session && recSessionRef.current?.id === session.id) resetCurrentRecSession({ keepDraft: !!resume });
      setRecPaused(false);
      setRecPauseSupported(false);
      stopRecordingStream(stream);
      stopRecordingStream(pc?.stream);   // 섞기 전에 실패한 경우
      alertDialog('마이크 접근 실패: ' + e.message);
    } finally {
      setRecStartingLocked(false);
    }
  }, [cleanupRecMeter, clearRecPreview, closeAliveNotifications, finalizeRecSession, flushPersist, pickRecMime, recording, resetCurrentRecSession, setAliveCheckMinLocked, setRecStartingLocked, setupPersist, setupResumePersist, startRecMeter, syncRecTimingRefs, updateRecTitle]);

  const pauseRec = useCallback(() => {
    const session = recSessionRef.current;
    const mr = session?.mr || mediaRef.current;
    if (!session || recFinalizingRef.current || !mr || typeof mr.pause !== 'function' || mr.state !== 'recording') return;
    try {
      mr.pause();
      session.paused = true;
      session.pauseStartedAt = Date.now();
      syncRecTimingRefs(session);
      setRecPaused(true);
      setRecLevel(0);
      setElapsedSec(Math.max(0, Math.floor(getRecordingAudioSec())));
    } catch {}
  }, [getRecordingAudioSec, syncRecTimingRefs]);

  const resumeRec = useCallback(() => {
    const session = recSessionRef.current;
    const mr = session?.mr || mediaRef.current;
    if (!session || recFinalizingRef.current || !mr || typeof mr.resume !== 'function' || mr.state !== 'paused') return;
    try {
      mr.resume();
      closeOpenRecPause(session);
      setElapsedSec(Math.max(0, Math.floor(getRecordingAudioSec())));
    } catch {}
  }, [closeOpenRecPause, getRecordingAudioSec]);

  const requestStopRec = useCallback((cancelled, options = {}) => {
    const session = recSessionRef.current;
    if (!session || recFinalizingRef.current) return;
    session.cancelled = !!cancelled;
    session.autoUpload = !!options.autoUpload;
    session.title = recTitleRef.current;
    setRecFinalizingLocked(true);
    setRecording(false);
    setElapsedSec(Math.max(0, Math.floor(getRecordingAudioSec())));
    try {
      if (session.mr && session.mr.state !== 'inactive') session.mr.stop();
      else finalizeRecSession(session);
    } catch {
      finalizeRecSession(session);
    }
  }, [finalizeRecSession, getRecordingAudioSec, setRecFinalizingLocked]);

  const stopRec = useCallback((options = {}) => requestStopRec(false, options), [requestStopRec]);
  // 무음 감지 tick 은 stopRec 보다 먼저 만들어지므로 ref 로 이어준다.
  // (렌더 중에 ref 를 건드리지 않도록 커밋 후에 심는다 — 미터는 startRec 이후에나 돌기 때문에 늦지 않다.)
  useEffect(() => {
    stopRecRef.current = stopRec;
  }, [stopRec]);

  // 무음 경고를 사용자가 걷는다("계속 녹음"). 다시 7분을 센다.
  const keepRecording = useCallback(() => {
    lastSoundAtRef.current = Date.now();
    silenceWarnRef.current = false;
    silenceWarnStartedRef.current = 0;
    setSilenceWarnSec(null);
  }, []);

  // 🔴 미리보기 조작 세 곳(버리기·업로드·다시 녹음)은 [이어 녹음] 시작 중엔 바로 돌아간다 —
  //    시작하는 동안 미리보기는 잠겨 있다(그 미리보기의 보관 행에 새 구간을 붙이는 중이다).
  const discardRecPreview = useCallback(() => {
    if (!recPreviewRef.current || recFinalizingRef.current || recStartingRef.current) return false;
    const { persistId } = recPreviewRef.current;
    if (persistId) void dropPersisted(persistId);   // 사용자가 버린 녹음 — 사본도 지운다
    clearRecPreview();
    setRecordingNotes([]);
    updateRecTitle('');
    setElapsedSec(0);
    return true;
  }, [clearRecPreview, dropPersisted, updateRecTitle]);

  const uploadRecPreview = useCallback(() => {
    const preview = recPreviewRef.current;
    if (!preview || recFinalizingRef.current || recStartingRef.current) return;
    const title = (recTitleRef.current || preview.title || '').trim() || preview.title || ('녹음 ' + new Date().toLocaleString('ko-KR'));
    const queued = enqueueUpload({
      blob: preview.blob,
      filename: preview.filename,
      title,
      notes: preview.notes,
      meta: recMetaRef.current,
      source: 'record',
      persistId: preview.persistId || null,
      segments: Array.isArray(preview.segments) ? preview.segments : null,
    });
    if (!queued) return;
    clearRecPreview();
    setRecordingNotes([]);
    updateRecTitle('');
    setElapsedSec(0);
  }, [clearRecPreview, enqueueUpload, updateRecTitle]);

  const restartRec = useCallback(async () => {
    if (recStartingRef.current) return;
    // 🔴 복구물(이어 녹음 결과 포함)은 [다시 녹음]으로 지우지 않는다. 확인창으로 묻지 않는다 — AppDialog 는
    //    확인 버튼에 포커스를 둬서 Enter 한 번이 되돌릴 수 없는 삭제가 된다.
    if (recPreviewRef.current?.recovered) {
      await alertDialog('[이어 녹음]으로 이어서 녹음하거나, [업로드]로 저장하거나, 필요 없으면 [취소]로 지운 뒤 새로 녹음해 주세요.');
      return;
    }
    if (!discardRecPreview()) return;
    await startRec(recMetaRef.current);
  }, [discardRecPreview, startRec]);

  // 복구 미리보기의 [이어 녹음] — 같은 persistId 에 새 MediaRecorder 구간을 이어 보관한다.
  // 미리보기·잠금은 녹음이 실제로 시작(mr.start)된 뒤에야 걷는다(startRec 의 resume 경로).
  const continueRecoveredRec = useCallback(async () => {
    const pv = recPreviewRef.current;
    if (!canResumePreview(pv)) return false;
    if (recStartingRef.current || recFinalizingRef.current || recSessionRef.current) return false;
    if (!persistLocksRef.current.has(pv.persistId)) return false;
    const resume = {
      persistId: pv.persistId,
      pv,
      seqBase: null,             // startRec 이 시작 잠금 뒤 lastSeq 로 채운다(null = 보관 안 함)
      discarded: false,
      discardPromise: null,
      title: recTitleRef.current || pv.title || '',
      notes: Array.isArray(pv.notes) ? [...pv.notes] : [],
      persistSegments: Array.isArray(pv.persistSegments) ? [...pv.persistSegments] : [],
    };
    await startRec(recMetaRef.current, { resume });
    return true;
  }, [startRec]);

  const cancelRec = useCallback(async () => {
    if (recPreviewRef.current) {
      if (recStartingRef.current) return;
      if (!(await confirmDialog('녹음을 취소하면 저장되지 않습니다. 취소할까요?'))) return;
      discardRecPreview();
      return;
    }
    // 이어 녹음 중 [취소] = 이어 녹음한 부분만 버린다(앞서 복구한 녹음은 남긴다 · 전체 삭제는 미리보기의 [취소]에서만).
    const resumeTarget = recSessionRef.current?.resume ? recSessionRef.current : null;
    if (resumeTarget) {
      if (!(await confirmDialog('이어 녹음한 부분만 버리고, 앞서 복구한 녹음은 남길까요?'))) return;
      // 확인 창이 떠 있는 동안 무음 자동중지 등으로 끝났을 수 있다 — 다시 읽는다.
      const session = recSessionRef.current;
      if (session !== resumeTarget || recFinalizingRef.current || session.finalized) return;
      const resume = session.resume;
      // 🔴 같은 동기 블록에서 먼저 닫는다 — 경계 patch 를 기다리는 보관 준비가 뒤늦게 persist 를 세우지 않게.
      resume.discarded = true;
      if (session.persist) session.persist.closed = true;
      // 🔴 dropPersisted(행 전체 삭제)를 부르지 않는다 — 새 구간 조각·경계만 한 트랜잭션에서 지운다.
      resume.discardPromise = resume.seqBase == null
        ? Promise.resolve(false)
        : discardTailSegment(resume.persistId, resume.seqBase, { notes: resume.notes, title: resume.title, state: 'stopped' })
          .catch(() => false);
      requestStopRec(true);   // finalize 가 reset 뒤 reviveResumePreview 로 미리보기를 되살린다
      return;
    }
    if (!(await confirmDialog('녹음을 취소하면 저장되지 않습니다. 취소할까요?'))) return;
    // 확인 창이 떠 있는 동안 무음 자동중지가 돌아 업로드로 넘어갔을 수 있다 — 다시 읽는다.
    const session = recSessionRef.current;
    if (session && !recFinalizingRef.current) {
      const persistId = session.persist?.id || null;
      if (session.persist) session.persist.closed = true;
      if (persistId) void dropPersisted(persistId);
    }
    requestStopRec(true);
  }, [discardRecPreview, dropPersisted, requestStopRec]);

  const forceClearRecording = useCallback(() => {
    const session = recSessionRef.current;
    if (session) {
      if (session.persist) session.persist.closed = true;
      session.cancelled = true;
      session.finalized = true;
      try {
        if (session.mr) {
          session.mr.onstop = null;
          session.mr.ondataavailable = null;
          if (session.mr.state !== 'inactive') session.mr.stop();
        }
      } catch {}
      try { stopRecordingStream(session.stream); } catch {}
    }
    // 🔴 여기서는 IndexedDB 를 지우지 않는다 — 이 경로의 사본이 사라지면 복구가 없다.
    //    진행 중(uploading)인 업로드의 잠금만 남기고 이 탭이 쥔 녹음 잠금을 푼다.
    recoveryRef.current.epoch += 1;
    releaseAllPersistLocks(new Set(
      pendingUploadsRef.current
        .filter((item) => item.status === 'uploading' && item.persistId)
        .map((item) => item.persistId)
    ));
    cleanupRecMeter();
    clearRecPreview();
    resetCurrentRecSession();
    setRecStartingLocked(false);
    setRecFinalizingLocked(false);
    clearPendingUploads();
    setFloatingError('');
  }, [cleanupRecMeter, clearPendingUploads, clearRecPreview, releaseAllPersistLocks, resetCurrentRecSession, setRecFinalizingLocked, setRecStartingLocked]);

  useEffect(() => () => {
    forceClearRecording();
  }, [forceClearRecording]);

  // 탭 제목에 녹음 상태를 띄운다. 플로팅 표시는 다른 탭·다른 앱으로 가면 안 보인다 —
  // 회의 끝나고 자리로 돌아가 메일을 보는 상황이 정확히 그 경우다. 탭 목록에서는 보인다.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!recording) {
      if (recBaseTitleRef.current) {
        document.title = recBaseTitleRef.current;
        recBaseTitleRef.current = '';
      }
      return undefined;
    }
    if (!recBaseTitleRef.current) recBaseTitleRef.current = document.title;
    const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
    const ss = String(elapsedSec % 60).padStart(2, '0');
    // 무음 경고가 우선이다(그쪽은 1분 뒤 자동 중지라 더 급하다).
    // 그다음이 미확인 30분 확인 — 단 일시정지 중에는 기존 '⏸ 일시정지' 를 유지한다.
    const head = silenceWarnSec != null
      ? `⚠ 곧 중지 ${silenceWarnSec}초`
      : (aliveCheckMin != null && !recPaused
        ? `❓ 녹음 중인가요? ${mm}:${ss}`
        : `${recPaused ? '⏸ 일시정지' : '● 녹음 중'} ${mm}:${ss}`);
    document.title = `${head} — ${recBaseTitleRef.current}`;
    return undefined;
  }, [recording, elapsedSec, recPaused, silenceWarnSec, aliveCheckMin]);

  // 녹음 중 언마운트되어도 제목은 되돌린다.
  useEffect(() => () => {
    if (typeof document !== 'undefined' && recBaseTitleRef.current) {
      document.title = recBaseTitleRef.current;
      recBaseTitleRef.current = '';
    }
  }, []);

  useEffect(() => {
    if (!(recording || recFinalizing || recPreview)) return;
    const tick = () => {
      if (recPreviewRef.current) {
        setElapsedSec(Math.max(0, Math.floor(recPreviewRef.current.durationSec || 0)));
      } else {
        setElapsedSec(Math.max(0, Math.floor(getRecordingAudioSec())));
      }
      // 30분마다 «아직 녹음 중인가요?» — 라이브 녹음일 때만(마무리 중·미리듣기 제외).
      // 🔴 여기서 녹음을 멈추거나 일시정지하지 않는다. 일시정지 중에는 audioMs 가 늘지 않아
      //    저절로 멈추고, 숨은 탭에서 tick 이 느려져도 벽시계 기준이라 1분쯤 늦게 뜰 뿐이다.
      const session = recSessionRef.current;
      if (session && !recFinalizingRef.current && !recPreviewRef.current) {
        const audioMs = getRecordingAudioMs();
        if (audioMs >= aliveNextMsRef.current) {
          const minutes = Math.floor(audioMs / 60000);
          // 30분 단위를 건너뛰어도(숨은 탭) 한 번만 묻는다. 답이 없으면 분만 갱신한다(쌓지 않는다).
          aliveNextMsRef.current = (Math.floor(audioMs / ALIVE_CHECK_MS) + 1) * ALIVE_CHECK_MS;
          setAliveCheckMinLocked(minutes);
          // 🔴 알림은 다음 시점·미확인 기준값을 갱신한 **뒤**에 부른다 — 갱신 전에 값을 잡으면
          //    그 값이 곧 낡아(늦은 알림 판정) 알림이 영영 안 나간다.
          void notifyAliveCheck(minutes, session);
        }
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [getRecordingAudioMs, getRecordingAudioSec, notifyAliveCheck, recFinalizing, recPreview, recording, setAliveCheckMinLocked]);

  const shouldWarnRecordingExit = recording || !!recPreview || pendingUploads.length > 0 || (recFinalizing && !!recSessionRef.current && !recSessionRef.current.cancelled);
  useEffect(() => {
    if (!shouldWarnRecordingExit) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarnRecordingExit]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!localStorage.getItem('token') && (recording || recStartingRef.current || recFinalizingRef.current || recSessionRef.current || recPreviewRef.current || pendingUploadsRef.current.length > 0)) {
        forceClearRecording();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [forceClearRecording, recording]);

  // ── 주인 없는 녹음 복구 스캔 ──────────────────────────────────────────────
  // 잠금이 풀린 레코드 = 그 녹음을 들고 있던 탭이 죽었다는 뜻이다(새로고침·401·탭 닫기).
  // 로그인 상태에서 한가할 때 한 번만, **본인 것만** 묻는다.
  const runRecoveryScan = useCallback(async () => {
    const startedEpoch = recoveryRef.current.epoch;
    const startedUserId = currentUserId();
    if (!startedUserId) return;
    recoveryRef.current.done = true;
    recoveryRef.current.running = true;
    let heldId = null;
    const stillValid = () => (
      recoveryRef.current.epoch === startedEpoch
      && !!localStorage.getItem('token')
      && currentUserId() === startedUserId
    );
    const stop = () => {
      if (heldId) { releasePersistLock(heldId); heldId = null; }
      recoveryRef.current.done = false;   // 조건이 다시 맞으면 나중에 또 시도한다
    };
    try {
      const rows = await listSessions();
      if (!stillValid()) { stop(); return; }
      for (const row of rows) {
        const persistId = row?.persistId;
        if (!persistId || persistLocksRef.current.has(persistId)) continue;   // 이 탭이 쥔 녹음
        if (!isRecordingIdle()) { stop(); return; }
        let release = await acquireRecLock(persistId);
        if (!stillValid()) { if (release) { try { release(); } catch {} } stop(); return; }
        if (!release) {
          // 방금 새로고침한 페이지가 아직 잠금을 놓지 않았을 수 있다 — 한 번만 더 기다린다.
          await sleep(RECOVERY_LOCK_RETRY_MS);
          if (!stillValid()) { stop(); return; }
          release = await acquireRecLock(persistId);
          if (!stillValid()) { if (release) { try { release(); } catch {} } stop(); return; }
          if (!release) continue;   // 다른 탭에서 살아 있는 녹음이다
        }
        persistLocksRef.current.set(persistId, release);
        heldId = persistId;
        const chunkCount = await countChunks(persistId);
        if (!stillValid()) { stop(); return; }
        if (!chunkCount) { void dropPersisted(persistId); heldId = null; continue; }
        const owner = row.ownerUserId == null ? '' : String(row.ownerUserId);
        if (!owner || owner !== startedUserId) {
          // 공용 PC — 남의 녹음은 묻지 않는다. 30일이 지난 것만 지운다.
          if (isForeignExpired(row, Date.now())) void dropPersisted(persistId);
          else releasePersistLock(persistId);
          heldId = null;
          continue;
        }
        if (!isRecordingIdle()) { stop(); return; }
        const restore = await confirmDialog(recoveryPromptMessage(row, chunkCount), {
          title: '저장되지 않은 녹음이 있습니다',
          okText: '복구',
          cancelText: '나중에',
        });
        if (!stillValid()) { stop(); return; }
        if (!restore) { releasePersistLock(persistId); heldId = null; continue; }   // '나중에' = 보관
        const entries = await readChunkEntries(persistId);
        // 🔴 미리보기를 세우기 전 마지막 확인 — 여기부터 setRecPreview 까지 await 가 없다.
        if (!stillValid() || !isRecordingIdle()) { stop(); return; }
        // 이어 녹음 구간(segments)별로 나눈다 — 옛 행(segments 없음)은 구간 하나(row.mime)다.
        const split = splitSegments(row, entries);
        if (!split.length) { releasePersistLock(persistId); heldId = null; continue; }
        const preview = buildRecoveredPreview(row, split, persistId);
        // 🔴 펼치기만으로 끝내지 않는다 — 참석자·인식 언어가 없던 **옛 레코드**도 기본값으로
        //    채워지고, 규칙 밖 값이 섞여 있어도 업로드가 400 에 갇히지 않는다.
        recMetaRef.current = cleanRecMeta(row.meta);
        setRecLanguageState(recMetaRef.current.language);
        recPreviewRef.current = preview;
        setRecPreview(preview);
        // 🔴 이 갈래 하나만 — 미리보기를 세우는 공통 자리에 두면 멈춤 미리보기도 /meetings 로 끌고 간다.
        setRecoverNavSeq((n) => n + 1);
        updateRecTitle(preview.title);
        setRecordingNotes(preview.notes);
        heldId = null;   // 잠금은 이 탭이 계속 쥔다(미리보기·업로드가 끝날 때까지)
        return;          // 미리보기 칸은 하나다 — 나머지는 다음 로그인·로드 때 묻는다
      }
    } catch (e) {
      if (heldId) { releasePersistLock(heldId); heldId = null; }
    } finally {
      recoveryRef.current.running = false;
    }
  }, [currentUserId, dropPersisted, isRecordingIdle, releasePersistLock, updateRecTitle]);

  useEffect(() => {
    // jsdom·비보안 컨텍스트에는 IndexedDB·Web Locks 가 없다 → 인터벌 자체를 만들지 않는다.
    if (!isRecordingPersistSupported()) return undefined;
    const t = setInterval(() => {
      const state = recoveryRef.current;
      if (!localStorage.getItem('token')) {
        state.done = false;
        if (state.running) state.epoch += 1;   // 로그아웃되면 돌고 있는 스캔을 중단시킨다
        return;
      }
      if (state.done || state.running) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (!currentUserId() || !isRecordingIdle()) return;
      void runRecoveryScan();
    }, RECOVERY_SCAN_INTERVAL_MS);
    return () => clearInterval(t);
  }, [currentUserId, isRecordingIdle, runRecoveryScan]);

  const recordingSec = recPreview ? Math.max(0, Math.floor(recPreview.durationSec || 0)) : elapsedSec;
  const recordingScreenOpen = recording || !!recPreview || (recFinalizing && !!recSessionRef.current);
  const recordingBusy = recordingScreenOpen || recStarting || recFinalizing;
  const pendingUpload = pendingUploads[0] || null;
  const uploadingUploads = pendingUploads.filter((item) => item.status === 'uploading');
  // Multiple background uploads can coexist; the aggregate pct intentionally follows the newest active one.
  const uploadPct = uploadingUploads[uploadingUploads.length - 1]?.pct ?? null;
  // 미리보기에 [이어 녹음]을 보일까(복구물 · 보관 가능 · 구간 20개 미만 · webm/mp4/ogg).
  const recResumable = canResumePreview(recPreview);

  const value = useMemo(() => ({
    recording,
    recStarting,
    recFinalizing,
    recPreview,
    recResumable,
    continueRecoveredRec,
    recTitle,
    setRecTitle: updateRecTitle,
    recordingSec,
    recPaused,
    recPauseSupported,
    recLevel,
    silenceWarnSec,
    meterFailed,
    noSoundWarn,
    keepRecording,
    aliveCheckMin,
    confirmAlive,
    recordingNotes,
    addRecordingNote,
    removeRecordingNote,
    pauseRec,
    resumeRec,
    stopRec,
    uploadRecPreview,
    restartRec,
    cancelRec,
    pendingUpload,
    pendingUploads,
    retryPendingUpload,
    discardPendingUpload,
    uploadPct,
    uploadVersion,
    recordingScreenOpen,
    recordingScreenMinimized,
    minimizeRecordingScreen,
    restoreRecordingScreen,
    recoverNavSeq,
    recordingBusy,
    floatingError,
    recordingFolderName: recMetaRef.current.folderName || '',
    recLanguage,
    setRecLanguage,
    startRec,
  }), [
    addRecordingNote,
    cancelRec,
    continueRecoveredRec,
    floatingError,
    minimizeRecordingScreen,
    pauseRec,
    pendingUpload,
    pendingUploads,
    recFinalizing,
    recLanguage,
    recLevel,
    recPauseSupported,
    recPaused,
    recPreview,
    recResumable,
    recStarting,
    recTitle,
    recoverNavSeq,
    recording,
    recordingBusy,
    recordingScreenMinimized,
    silenceWarnSec,
    meterFailed,
    noSoundWarn,
    keepRecording,
    aliveCheckMin,
    confirmAlive,
    recordingNotes,
    recordingScreenOpen,
    recordingSec,
    removeRecordingNote,
    restartRec,
    retryPendingUpload,
    restoreRecordingScreen,
    resumeRec,
    setRecLanguage,
    startRec,
    stopRec,
    updateRecTitle,
    uploadPct,
    uploadRecPreview,
    uploadVersion,
    discardPendingUpload,
  ]);

  return (
    <RecordingContext.Provider value={value}>
      {children}
      {/* 🔴 App.jsx 가 아니라 여기서 그린다 — RecordingContext 를 ({children}) => children 목으로
          막아 둔 App 렌더 시험들이 훅을 쓰는 카드 때문에 깨지지 않게(표시 전용 컴포넌트라
          그 목에서는 아예 그려지지 않는다). Provider 가 앱 전체를 감싸므로 어느 페이지에서든 뜬다.
          무음 경고가 떠 있으면 그쪽(1분 뒤 자동중지)이 앞서므로 카드는 숨긴다 — 미확인 상태는
          그대로 남아 무음이 걷히면 다시 보인다. */}
      <RecordingAliveCheck
        minutes={silenceWarnSec == null ? aliveCheckMin : null}
        onContinue={confirmAlive}
        onStop={() => stopRec({ autoUpload: true })}
      />
    </RecordingContext.Provider>
  );
}

export function useRecording() {
  const ctx = useContext(RecordingContext);
  if (!ctx) throw new Error('useRecording must be used within RecordingProvider');
  return ctx;
}
